import { useSelector, useDispatch } from 'react-redux';
import { selectIsAuthenticated, changeFilterOptions, setFilterOptions } from '../app/redux/slices/authSlice';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getScrollInfoV2, fetchUserInfo, selectCurrentTeam, fetchTrapInfo, updateTrap, fetchDataLst, setCurrentTeam } from '../app/redux/slices/dataSlice';
import { fetchEnhancedUserInfo } from '../app/redux/slices/masterSubAccountSlice';
import { AppDispatch, RootState } from '../app/redux/store';
import ProbeDataView from '../app/components/ProbeDataView';
import Navigation from '../app/components/NavigationSPA';
import FilterModal from '../app/components/FilterModal';
import InsectCountChart from '../app/components/InsectCountChart';
import fallbackIcon from '../app/assets/images/icon.png';
import BatteryIndicator from '../app/components/BatteryIndicator';
import WifiIndicator from '../app/components/WifiIndicator';
import InsectCountIndicator from '../app/components/InsectCountIndicator';
import { useTranslation } from 'react-i18next';
import { logger } from '../app/utils/logger';

// Define types for the data

interface ProbeDataItem {
  insectCollection?: {
    beetle?: number | null;
    moth?: number | null;
    weevil?: number | null;
    other?: number | null;
  } | null;
  particleCount: number;
  createdAt: string;
  temperature?: string;
  humidity?: string;
  trapId: string;
  building: string | null;
  location: string | null;
  wifiInfo: string;
  batteryInfo: string;
  probeModel: string;
  fileName: string;
  originalFileUrl: string;
  updatedAt: string;
  _id: string;
  __v: number;
}

interface Trap {
  trapId: string;
  trapName: string | null;
  location: string | null;
  building: string | null;
  originalFileUrl: string | null;
  offlineModeStatus: 'offlineMode' | 'wifiMode' | 'resetFactoryMode';
  wifiInfo: number | null;
  batteryInfo: number | null;
  probeModel: string | null;
  insectCollection: {
    moth: number | null;
    beetle: number | null;
    other: number | null;
  } | null;
  frequency: string | null;
  brightness: string | null;
  minutes: string | null;
  hours: string | null;
  days: string | null;
  pwm: string | null;
  lastUpdated: string | null;
  lastestImageDate: string | null;
}

interface TrapInfoResponse {
  trap: {
    id: string;
    trapName: string;
    location: string;
    building: string;
    macAddress: string;
    probeModel: string;
    frequency: string;
    brightness: string;
    pwm: string;
    minutes: string;
    hours: string;
    days: string;
    subscriptionData: {
      subscriptionType: string;
    };
    yellow: number;
    lightRed: number;
    darkRed: number;
  };
}



interface EditModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  currentValue: string;
  onSave: (value: string) => void;
}

const EditModal = ({ isOpen, onClose, title, currentValue, onSave }: EditModalProps) => {
  const [value, setValue] = useState(currentValue);
  const { t } = useTranslation(['common', 'modal']);

  useEffect(() => {
    setValue(currentValue);
  }, [currentValue]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-96">
        <h3 className="text-lg font-semibold mb-4">{t('common:edit')} {title}</h3>
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="w-full p-2 border rounded mb-4"
        />
        <div className="flex justify-end space-x-2">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-900 rounded hover:bg-gray-300"
          >
            {t('common:cancel')}
          </button>
          <button
            onClick={() => {
              onSave(value);
              onClose();
            }}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            {t('common:save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function DashboardPage() {
  const currentTeam = useSelector(selectCurrentTeam);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { filter, ascend, isSubAccount } = useSelector((state: RootState) => state.auth);
  const { userInfo } = useSelector((state: RootState) => state.data);
  const { isMasterAccount, allTeamsData } = useSelector((state: RootState) => state.masterSubAccount);
  const { t, i18n } = useTranslation(['dashboard', 'common']);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [mounted, setMounted] = useState(false);
  const [selectedProbeId, setSelectedProbeId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [searchTerm, setSearchTerm] = useState('');
  const [searchField, setSearchField] = useState<'trapId' | 'trapName' | 'location' | 'building'>('trapId');
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isFilterLoading, setIsFilterLoading] = useState(false);

  // Get current locale for date formatting
  const getDateLocale = () => {
    return i18n.language === 'zh' ? 'zh-CN' : 'en-US';
  };
  const [editModal, setEditModal] = useState<{
    isOpen: boolean;
    field: string;
    title: string;
    value: string;
    trapId: string;
  }>({
    isOpen: false,
    field: '',
    title: '',
    value: '',
    trapId: '',
  });

  // Add traps state to replace scrollInfo
  const [traps, setTraps] = useState<Trap[]>([]);
  
  // Add state for chart data
  const [chartData, setChartData] = useState<ProbeDataItem[]>([]);

  // Handle hydration
  useEffect(() => {
    setMounted(true);
  }, []);

  // Ensure enhanced user info is loaded (for master accounts ONLY)
  useEffect(() => {
    if (mounted && isAuthenticated && !isSubAccount && !allTeamsData) {
      logger.log('📊 Dashboard: fetching enhanced user info (not a sub-account)...');
      dispatch(fetchEnhancedUserInfo()).catch((error) => {
        logger.warn('Dashboard: Failed to fetch enhanced user info:', error);
      });
    } else if (mounted && isSubAccount) {
      logger.log('⏭️ Dashboard: Skipping fetchEnhancedUserInfo (sub-account detected)');
    }
  }, [mounted, isAuthenticated, isSubAccount, allTeamsData, dispatch]);

  // Initialize team as default on page load if no team is selected
  useEffect(() => {
    logger.log('🔍 Dashboard team initialization check:', {
      mounted,
      isMasterAccount,
      hasAllTeamsData: !!allTeamsData,
      allTeamsDataLength: allTeamsData?.length,
      hasCurrentTeam: !!currentTeam,
      currentTeamName: currentTeam?.name,
      hasUserInfo: !!userInfo,
      userInfoTeams: userInfo?.teams
    });
    
    // For master accounts, use allTeamsData first, then fallback to userInfo.teams
    if (mounted && isMasterAccount && !currentTeam) {
      if (allTeamsData && allTeamsData.length > 0) {
        logger.log('📍 Master account: No team selected on page load, setting master team from allTeamsData');
        logger.log('📋 allTeamsData:', JSON.stringify(allTeamsData, null, 2));
        const masterTeam = allTeamsData.find(t => t.type === 'master');
        if (masterTeam) {
          logger.log('✅ Found master team:', JSON.stringify(masterTeam, null, 2));
          logger.log('✅ Setting team with id:', masterTeam.id, 'name:', masterTeam.name);
          const teamToSet = { id: masterTeam.id, name: masterTeam.name };
          logger.log('✅ Team object to dispatch:', teamToSet);
          dispatch(setCurrentTeam(teamToSet));
        } else {
          logger.log('⚠️ No master team found in allTeamsData');
          logger.log('⚠️ Available teams:', allTeamsData.map(t => ({ type: t.type, name: t.name })));
          // Fallback to userInfo.teams
          if (userInfo && userInfo.teams && userInfo.teams.length > 0) {
            logger.log('📍 Master account: Falling back to userInfo.teams');
            dispatch(setCurrentTeam({ 
              id: userInfo.teams[0], 
              name: userInfo.teams[0] 
            }));
          }
        }
      } else if (userInfo && userInfo.teams && userInfo.teams.length > 0) {
        // Fallback to userInfo.teams if allTeamsData is not loaded yet
        logger.log('📍 Master account: allTeamsData not loaded, using userInfo.teams as fallback');
        dispatch(setCurrentTeam({ 
          id: userInfo.teams[0], 
          name: userInfo.teams[0] 
        }));
      }
    }
    // For regular users, use userInfo teams (fallback if currentTeam is not set by fetchUserInfo)
    else if (mounted && !isMasterAccount && userInfo && userInfo.teams && userInfo.teams.length > 0 && !currentTeam) {
      logger.log('📍 Regular user: No team selected, setting first team from userInfo');
      dispatch(setCurrentTeam({ 
        id: userInfo.teams[0], 
        name: userInfo.teams[0] 
      }));
    }
  }, [mounted, isMasterAccount, allTeamsData, currentTeam, userInfo, dispatch]);

  // Filter application function (simplified - auto refetch handles data update)
  const handleFilterApply = async (newFilter: string, newAscend: boolean) => {
    setIsFilterLoading(true);
    try {
      logger.log('Applying filter:', { filter: newFilter, ascend: newAscend });
      
      // Update filter settings - the useEffect will automatically refetch data
      await dispatch(changeFilterOptions({ filter: newFilter, ascend: newAscend })).unwrap();
      
      // Reset to first page
      setCurrentPage(1);
      
    } catch (error) {
      logger.error('Failed to apply filter:', error);
      // Even if backend update fails, try local update for immediate UI response
      localStorage.setItem("userFilter", newFilter);
      localStorage.setItem("userAscend", newAscend.toString());
      
      // Force update Redux state locally to trigger auto-refetch
      logger.log('Forcing local Redux state update');
      dispatch(setFilterOptions({ filter: newFilter, ascend: newAscend }));
    } finally {
      setIsFilterLoading(false);
    }
  };

  // Refresh function that maintains current page and selection
  const handleRefresh = async () => {
    logger.log('🔄 Refresh button clicked');
    logger.log('🔄 Current team:', currentTeam);
    logger.log('🔄 Current isLoading:', isLoading);
    logger.log('🔄 isMasterAccount:', isMasterAccount);
    logger.log('🔄 allTeamsData:', allTeamsData);
    logger.log('🔄 userInfo:', userInfo);
    
    // Prevent multiple simultaneous refreshes
    if (isLoading) {
      logger.log('⏭️ Refresh already in progress, skipping...');
      return;
    }
    
    setIsLoading(true);
    try {
      // Try to determine which team to use
      let teamNameToUse: string | null = null;
      
      // First, try to fetch teams if they're not loaded yet (for master accounts)
      if (!currentTeam?.name && isMasterAccount && !allTeamsData) {
        logger.log('⏳ Teams not loaded yet, fetching enhanced user info...');
        try {
          await dispatch(fetchEnhancedUserInfo()).unwrap();
          // Wait a bit for Redux state to update
          await new Promise(resolve => setTimeout(resolve, 100));
        } catch (error) {
          logger.warn('⚠️ Failed to fetch enhanced user info:', error);
        }
      }
      
      // Re-check after potential fetch
      if (currentTeam?.name) {
        teamNameToUse = currentTeam.name;
        logger.log('✅ Using currentTeam:', teamNameToUse);
      } else if (isMasterAccount && allTeamsData && allTeamsData.length > 0) {
        // For master accounts, try to use the master team
        const masterTeam = allTeamsData.find(t => t.type === 'master');
        if (masterTeam) {
          teamNameToUse = masterTeam.name;
          logger.log('✅ Found master team from allTeamsData:', teamNameToUse);
          // Also set it in Redux for future use
          dispatch(setCurrentTeam({ id: masterTeam.id, name: masterTeam.name }));
        } else {
          logger.warn('⚠️ No master team found in allTeamsData');
        }
      } else if (userInfo?.teams && userInfo.teams.length > 0) {
        // For regular users, use the first team from userInfo
        teamNameToUse = userInfo.teams[0];
        logger.log('✅ Using first team from userInfo:', teamNameToUse);
        // Also set it in Redux for future use
        dispatch(setCurrentTeam({ id: userInfo.teams[0], name: userInfo.teams[0] }));
      }
      
      if (!teamNameToUse) {
        logger.error('❌ No team available, cannot refresh');
        logger.error('❌ Debug info:', {
          hasCurrentTeam: !!currentTeam,
          currentTeamName: currentTeam?.name,
          isMasterAccount,
          hasAllTeamsData: !!allTeamsData,
          allTeamsDataLength: allTeamsData?.length,
          hasUserInfo: !!userInfo,
          userInfoTeams: userInfo?.teams
        });
        alert('No team available. Please wait for the page to finish loading or select a team.\n\nCheck the debug panel above for more details.');
        return;
      }
      
      const userFilter = filter || "id";
      const userAscend = ascend !== null ? ascend : true;
      
      logger.log('🔄 Refreshing with:', {
        teamName: teamNameToUse,
        filter: userFilter,
        ascend: userAscend
      });
      
      const result = await dispatch(getScrollInfoV2({
        teamName: teamNameToUse,
        filter: userFilter,
        ascend: userAscend,
        page: 1,
        pageSize: 20
      })).unwrap();
      
      logger.log('✅ Refresh successful, received traps:', result.traps?.length || 0);
      
      if (result?.traps) {
        setTraps(result.traps);
        // Reset to first page when refreshing
        setCurrentPage(1);
        
        // Keep the same selected probe if it still exists
        if (selectedProbeId && !result.traps.find((trap: Trap) => trap.trapId === selectedProbeId)) {
          // If selected probe no longer exists, select the first one
          if (result.traps.length > 0) {
            setSelectedProbeId(result.traps[0].trapId);
          } else {
            setSelectedProbeId(null);
          }
        }
      } else {
        logger.warn('⚠️ Refresh response missing traps array');
        setTraps([]);
      }
    } catch (error) {
      logger.error('❌ Failed to refresh data:', error);
      alert('Failed to refresh data. Please try again.');
    } finally {
      setIsLoading(false);
      logger.log('🔄 Refresh complete, isLoading set to false');
    }
  };

  // Add useEffect to clear selected probe when team changes
  useEffect(() => {
    logger.log('🔄 Team changed, clearing selected probe');
    logger.log('🔄 New team:', currentTeam);
    setSelectedProbeId(null);
  }, [currentTeam]);

  useEffect(() => {
    logger.log('📊 Main data fetch useEffect triggered');
    logger.log('📊 Dependencies:', { mounted, isAuthenticated, currentTeam });
    
    const fetchData = async () => {
      if (!mounted) {
        logger.log('⏭️ Skipping: not mounted');
        return;
      }
      
      if (!isAuthenticated) {
        logger.log('⏭️ Redirecting to login');
        navigate('/login');
        return;
      }

      logger.log('📊 Fetching user info and SmartProbes...');
      try {
        // First fetch user info to get filter settings
        const userInfo = await dispatch(fetchUserInfo()).unwrap();
        
        if (currentTeam?.name) {
          logger.log('📊 Current team name:', currentTeam.name);
          // Use filter settings from user info, fallback to Redux state, then defaults
          const userFilter = userInfo.userInfo.filter || filter || "id";
          const userAscend = userInfo.userInfo.ascend !== null ? userInfo.userInfo.ascend : (ascend !== null ? ascend : true);
          
          const result = await dispatch(getScrollInfoV2({
            teamName: currentTeam.name,
            filter: userFilter,
            ascend: userAscend,
            page: 1,
            pageSize: 20
          })).unwrap();
          
          setTraps(result.traps);
          
          // Always select the first probe when team changes or if none is selected
          if (result.traps.length > 0) {
            logger.log('📍 Selecting first probe:', result.traps[0].trapId);
            logger.log('📍 Current team when selecting probe:', currentTeam);
            logger.log('📍 Current team name:', currentTeam?.name);
            setSelectedProbeId(result.traps[0].trapId);
          }
        }
      } catch (error) {
        logger.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [dispatch, currentTeam, isAuthenticated, navigate, mounted, filter, ascend]);

  // Auto-refetch when filter/ascend changes (same as mobile app HomeScreen.js lines 31-41)
  useEffect(() => {
    logger.log('Auto-refetch useEffect triggered with:', { 
      filter, 
      ascend, 
      teamName: currentTeam?.name, 
      isLoading 
    });
    
    const refetchWithFilter = async () => {
      if (currentTeam?.name && !isLoading && mounted) {
        try {
          // Use the exact same endpoint and payload structure as mobile app
          const result = await dispatch(getScrollInfoV2({
            teamName: currentTeam.name,
            filter: filter || "id",
            ascend: ascend !== null ? ascend : true,
            page: 1,
            pageSize: 20
          })).unwrap();
          
          if (result?.traps) {
            setTraps(result.traps);
          }
        } catch (error) {
          logger.error('Failed to refetch with new filter:', error);
        }
      } else {
        logger.log('Skipping refetch because:', {
          hasTeam: !!currentTeam?.name,
          isLoading
        });
      }
    };

    refetchWithFilter();
  }, [filter, ascend, currentTeam?.name, dispatch, isLoading, mounted]);



  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const getFilteredTraps = () => {
    if (!traps.length) return [];
    
    // Apply search filter
    let filteredTraps = traps;
    if (searchTerm) {
      filteredTraps = traps.filter(trap => {
        const searchValue = trap[searchField]?.toLowerCase() || '';
        return searchValue.includes(searchTerm.toLowerCase());
      });
    }
    
    return filteredTraps;
  };

  const getPaginatedTraps = () => {
    const filteredTraps = getFilteredTraps();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredTraps.slice(startIndex, endIndex);
  };

  const totalPages = traps.length ? Math.ceil(getFilteredTraps().length / itemsPerPage) : 0;



  const handleSave = async (value: string) => {
    if (!editModal.trapId) return;

    const trap = traps.find(t => t.trapId === editModal.trapId);
    if (!trap) return;

    const updatePayload = {
      id: editModal.trapId,
      trapName: editModal.field === 'trapName' ? value : trap.trapName || '',
      location: editModal.field === 'location' ? value : trap.location || '',
      building: editModal.field === 'building' ? value : trap.building || '',
      frequency: trap.frequency || '',
      brightness: trap.brightness || '',
      minutes: trap.minutes || '',
      hours: trap.hours || '',
      days: trap.days || '',
      pwm: trap.pwm || '',
    };

    try {
      setIsLoading(true);
      
      // First make the API call
      await dispatch(updateTrap(updatePayload)).unwrap();

      // Store the current trap ID to maintain selection
      const currentTrapId = editModal.trapId;

      // Wait for 1 second to allow the server to process the update
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Then refresh both the list and details data
      const [listResult, trapInfo] = await Promise.all([
        dispatch(getScrollInfoV2({
          teamName: currentTeam?.name || '',
          filter: filter || "id",
          ascend: ascend !== null ? ascend : true,
          page: 1,
          pageSize: 20
        })).unwrap(),
        dispatch(fetchTrapInfo(currentTrapId)).unwrap()
      ]);

      // Update both states
      setTraps(listResult.traps);
      setTrapInfo(trapInfo);
      
      // Ensure the same trap remains selected
      setSelectedProbeId(currentTrapId);

      // Force a re-render of the list
      setCurrentPage(currentPage);
      
    } catch (error) {
      logger.error('Failed to update trap:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Add trapInfo state
  const [trapInfo, setTrapInfo] = useState<TrapInfoResponse | null>(null);

  // Update useEffect to fetch trap info and chart data when a probe is selected
  useEffect(() => {
    logger.log('🔍 Probe details fetch check:', {
      selectedProbeId,
      currentTeam: currentTeam,
      hasTeam: !!currentTeam?.name,
      teamName: currentTeam?.name
    });
    
    const fetchTrapDetails = async () => {
      if (selectedProbeId && currentTeam?.name) {
        logger.log('📥 Fetching details for probe:', selectedProbeId, 'team:', currentTeam.name);
        try {
          const result = await dispatch(fetchTrapInfo(selectedProbeId)).unwrap();
          setTrapInfo(result);
          logger.log('✅ Probe info loaded:', result.trap?.trapName);
          
          // Fetch chart data
          const queryString = `${selectedProbeId}?teamName=${currentTeam.name}&filter=id&ascend=true`;
          const chartResult = await dispatch(fetchDataLst(queryString)).unwrap();
          setChartData(chartResult?.trap?.dataLst || []);
          logger.log('✅ Chart data loaded:', chartResult?.trap?.dataLst?.length, 'items');
        } catch (error) {
          logger.error('❌ Failed to fetch trap details:', error);
        }
      } else {
        logger.log('⏭️ Skipping probe details fetch - no probe or team', {
          hasProbe: !!selectedProbeId,
          hasTeamName: !!currentTeam?.name
        });
        // Clear chart data when no probe is selected
        setChartData([]);
        setTrapInfo(null);
      }
    };

    fetchTrapDetails();
  }, [selectedProbeId, dispatch, currentTeam]);

  // Prevent hydration mismatch
  if (!mounted) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Navigation />
        <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0">
            <div className="flex justify-center items-center h-64">
              <div className="text-gray-800">{t('common:loading')}</div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Navigation />

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Probe Data View - Left Column */}
            <div className="lg:col-span-3">
              <ProbeDataView 
                probeId={selectedProbeId} 
                teamName={currentTeam?.name || ''} 
                trapInfo={trapInfo}
              />
            </div>

            {/* SmartProbes - Right Column */}
            <div className="lg:col-span-2">
              <div className="bg-white shadow rounded-lg p-6 flex flex-col" style={{ height: 'calc(100vh - 8rem)' }}>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-bold">{t('dashboard:mySmartProbes')}</h2>
                  <button
                    onClick={() => setIsFilterModalOpen(true)}
                    disabled={isFilterLoading}
                    className={`px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-300 flex items-center space-x-2 ${
                      isFilterLoading 
                        ? 'bg-gray-200 cursor-not-allowed text-gray-800' 
                        : 'bg-gray-300 text-gray-900 hover:bg-gray-400 cursor-pointer'
                    }`}
                  >
                    {isFilterLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                      </svg>
                    )}
                    <span>
                      {isFilterLoading ? t('dashboard:loading') : `${t('dashboard:filter')} (${
                        (filter || 'id') === 'id' ? 'ID' :
                        (filter || 'id') === 'name' ? t('modal:name') :
                        (filter || 'id') === 'building' ? t('dashboard:building') :
                        (filter || 'id') === 'location' ? t('dashboard:location') :
                        (filter || 'id') === 'favorite' ? t('modal:favorites') :
                        (filter || 'id').charAt(0).toUpperCase() + (filter || 'id').slice(1).toLowerCase()
                      } - ${ascend ? t('modal:ascending') : t('modal:descending')})`}
                    </span>
                  </button>
                </div>
                
                {/* Search and Filter Section with Refresh Button */}
                <div className="mb-4 flex flex-col space-y-3">
                  <div className="flex space-x-2">
                    <select
                      value={searchField}
                      onChange={(e) => {
                        setSearchField(e.target.value as 'trapId' | 'trapName' | 'location' | 'building');
                        setSearchTerm(''); // Clear search when changing field
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                    >
                      <option value="trapId">{t('dashboard:probeId')}</option>
                      <option value="trapName">{t('dashboard:probeName')}</option>
                      <option value="location">{t('dashboard:location')}</option>
                      <option value="building">{t('dashboard:building')}</option>
                    </select>
                    <div className="flex-1">
                      <input
                        type="text"
                        placeholder={`${
                          searchField === 'trapId' ? t('dashboard:probeId') :
                          searchField === 'trapName' ? t('dashboard:probeName') :
                          searchField === 'location' ? t('dashboard:location') :
                          t('dashboard:building')
                        }...`}
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setCurrentPage(1); // Reset to first page when searching
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    {/* Refresh Button - Always visible */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRefresh();
                      }}
                      disabled={isLoading}
                      className={`p-2 px-3 rounded-md flex items-center justify-center ${
                        isLoading 
                          ? 'bg-gray-200 cursor-not-allowed text-gray-800' 
                          : 'bg-green-500 text-white hover:bg-green-600 cursor-pointer'
                      }`}
                      title={t('dashboard:refresh')}
                    >
                      {isLoading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                      ) : (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {isLoading ? (
                  <div className="flex justify-center items-center flex-grow">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                  </div>
                ) : traps.length > 0 ? (
                  <div className="flex flex-col flex-1 min-h-0">
                    {/* Pagination Controls */}
                    <div className="mb-4 flex items-center justify-between border-b pb-4">
                      <div className="text-sm text-gray-900">
                        {t('dashboard:showing')} {Math.min((currentPage - 1) * itemsPerPage + 1, getFilteredTraps().length)} {t('dashboard:to')} {Math.min(currentPage * itemsPerPage, getFilteredTraps().length)} {t('dashboard:of')} {getFilteredTraps().length} {t('dashboard:items')}
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className={`px-3 py-1 rounded-md text-sm font-medium ${
                            currentPage === 1
                              ? 'bg-gray-200 text-gray-800 cursor-not-allowed'
                              : 'bg-blue-500 text-white hover:bg-blue-600 cursor-pointer'
                          }`}
                        >
                          {t('dashboard:prev')}
                        </button>
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className={`px-3 py-1 rounded-md text-sm font-medium ${
                            currentPage === totalPages
                              ? 'bg-gray-200 text-gray-800 cursor-not-allowed'
                              : 'bg-blue-500 text-white hover:bg-blue-600 cursor-pointer'
                          }`}
                        >
                          {t('dashboard:next')}
                        </button>
                      </div>
                    </div>

                    <div className="flex-1 overflow-y-auto space-y-3 pr-2" style={{ maxHeight: 'calc(100vh - 16rem)' }}>
                      {getPaginatedTraps().map((trap: Trap, index: number) => (
                        <div 
                          key={`${trap.trapId}-${index}`}
                          className={`border rounded-lg p-2 cursor-pointer transition-colors duration-150 ${
                            selectedProbeId === trap.trapId 
                              ? 'border-blue-500 bg-blue-50' 
                              : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                          }`}
                          onClick={() => setSelectedProbeId(trap.trapId)}
                        >
                          <div className="flex">
                            {/* Image taking full height */}
                            <div className="flex-shrink-0 mr-3 flex items-center">
                              {trap.originalFileUrl ? (
                                <img 
                                  src={trap.originalFileUrl} 
                                  alt={`${trap.trapName || trap.trapId} image`} 
                                  width={64}
                                  height={64}
                                  className="h-16 w-16 rounded-full object-cover"
                                />
                              ) : (
                                <img 
                                  src={fallbackIcon} 
                                  alt="No image available" 
                                  width={64}
                                  height={64}
                                  className="h-16 w-16 rounded-full object-cover"
                                />
                              )}
                            </div>
                            
                            {/* Content to the right of the image */}
                            <div className="flex flex-col justify-between flex-grow">
                              {/* Top row with probe ID/name and indicators */}
                              <div className="flex items-center justify-between">
                                <h3 className="font-medium text-gray-900 text-base">
                                  {trap.trapName || trap.trapId}
                                </h3>
                                <div className="flex items-center space-x-1">
                                  {trap.wifiInfo && <WifiIndicator iconvalue={trap.wifiInfo} size={28} />}
                                  {trap.batteryInfo && <BatteryIndicator count={trap.batteryInfo} model={trap.probeModel || undefined} size={36} />}
                                  <InsectCountIndicator 
                                    insectCollection={trap.insectCollection}
                                    yellowThreshold={userInfo?.yellow || 10}
                                    lightRedThreshold={userInfo?.lightRed || 20}
                                    darkRedThreshold={userInfo?.darkRed || 30}
                                    size={28}
                                    showCount={true}
                                  />
                                </div>
                              </div>
                              
                              {/* Bottom row with location, building, and latest data */}
                              <div className="flex flex-col space-y-0.5 text-xs text-gray-800">
                                <div>
                                  <span className="font-medium">{t('dashboard:locationLabel')}</span> {trap.location || '-'}
                                </div>
                                <div>
                                  <span className="font-medium">{t('dashboard:buildingLabel')}</span> {trap.building || '-'}
                                </div>
                                <div>
                                  <span className="font-medium">{t('dashboard:latestLabel')}</span> {
                                    trap.lastestImageDate 
                                      ? new Date(trap.lastestImageDate).toLocaleDateString(getDateLocale(), {
                                          month: 'numeric',
                                          day: 'numeric',
                                          year: '2-digit'
                                        })
                                      : '-'
                                  }
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-gray-800">No probes available</div>
                )}
              </div>
            </div>
          </div>
          
          {/* Insect Count Chart - Full Width */}
          {selectedProbeId && (
            <div className="mt-6">
              <div className="bg-white shadow rounded-lg p-6">
                <InsectCountChart 
                  data={chartData} 
                  probeId={selectedProbeId || 'Unknown'}
                  probeName={trapInfo?.trap?.trapName || ''}
                />
              </div>
            </div>
          )}
        </div>
      </main>

      <EditModal
        isOpen={editModal.isOpen}
        onClose={() => setEditModal({ ...editModal, isOpen: false })}
        title={editModal.title}
        currentValue={editModal.value}
        onSave={handleSave}
      />

      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onApply={handleFilterApply}
      />
    </div>
  );
}
